import { useEffect, useRef, useState } from "react";
import { FileArrowDown, FileText, Microphone, Pause, Play, Spinner } from "@phosphor-icons/react";
import { api } from "@/lib/apiClient";

// ─── Luxury Waveform Voice Player ───────────────────────────────────────────

function formatAudioTime(secs) {
    if (!secs || isNaN(secs)) return "0:00";
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
}

const FALLBACK_WAVEFORM = [5, 9, 14, 10, 18, 12, 7, 15, 11, 8, 16, 13, 6, 19, 10, 14, 8, 12, 17, 9, 13, 7, 11];

function VoiceNotePlayer({ blobUrl, attachment, mine }) {
    const audioRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [progress, setProgress] = useState(0); // 0–1
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(attachment.duration_ms ? attachment.duration_ms / 1000 : 0);

    const bars = (attachment.waveform && attachment.waveform.length > 0) ? attachment.waveform : FALLBACK_WAVEFORM;
    const accentColor = mine ? "#D4AF37" : "#8B9DC3";
    const dimColor = mine ? "rgba(212,175,55,0.25)" : "rgba(139,157,195,0.25)";

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;
        const onTime = () => {
            setCurrentTime(audio.currentTime);
            setProgress(audio.duration ? audio.currentTime / audio.duration : 0);
        };
        const onLoad = () => setDuration(audio.duration || 0);
        const onEnd = () => { setPlaying(false); setProgress(0); setCurrentTime(0); if (audio) audio.currentTime = 0; };
        audio.addEventListener("timeupdate", onTime);
        audio.addEventListener("loadedmetadata", onLoad);
        audio.addEventListener("ended", onEnd);
        return () => {
            audio.removeEventListener("timeupdate", onTime);
            audio.removeEventListener("loadedmetadata", onLoad);
            audio.removeEventListener("ended", onEnd);
        };
    }, []);

    const togglePlay = () => {
        const audio = audioRef.current;
        if (!audio) return;
        if (playing) { audio.pause(); setPlaying(false); }
        else { audio.play(); setPlaying(true); }
    };

    const handleWaveformClick = (e) => {
        const audio = audioRef.current;
        if (!audio || !audio.duration) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        audio.currentTime = ratio * audio.duration;
        setProgress(ratio);
    };

    const filled = Math.round(progress * bars.length);

    return (
        <div
            className="flex items-center gap-3 px-4 py-3 rounded-2xl min-w-[220px] max-w-[280px]"
            style={{
                background: mine ? "rgba(16,25,40,0.7)" : "rgba(255,255,255,0.05)",
                border: `1px solid ${mine ? "rgba(212,175,55,0.25)" : "rgba(255,255,255,0.08)"}`,
            }}
        >
            <audio ref={audioRef} src={blobUrl} preload="metadata" className="hidden" />

            {/* Play / Pause button */}
            <button
                onClick={togglePlay}
                className="shrink-0 w-9 h-9 rounded-full grid place-items-center transition-all duration-150 active:scale-95"
                style={{ background: accentColor, color: "#0a0a0a" }}
                aria-label={playing ? "Pause voice note" : "Play voice note"}
            >
                {playing ? <Pause size={16} weight="fill" /> : <Play size={16} weight="fill" />}
            </button>

            {/* Waveform + time */}
            <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                {/* Clickable waveform bars */}
                <div
                    className="flex items-end gap-[2px] h-7 cursor-pointer"
                    onClick={handleWaveformClick}
                    role="slider"
                    aria-label="Voice note scrubber"
                    aria-valuenow={Math.round(progress * 100)}
                    aria-valuemin={0}
                    aria-valuemax={100}
                >
                    {bars.map((val, i) => {
                        const heightPct = Math.max(12, Math.min(100, (val / 100) * 100));
                        const isFilled = i < filled;
                        return (
                            <div
                                key={i}
                                className="flex-1 rounded-full transition-all duration-75"
                                style={{
                                    height: `${heightPct}%`,
                                    background: isFilled ? accentColor : dimColor,
                                    minWidth: 2,
                                }}
                            />
                        );
                    })}
                </div>

                {/* Time display */}
                <div className="flex items-center justify-between">
                    <span className="text-[10px]" style={{ color: mine ? "rgba(212,175,55,0.7)" : "rgba(255,255,255,0.4)" }}>
                        <Microphone size={9} className="inline mr-1" />
                        Voice
                    </span>
                    <span className="text-[10px] tabular-nums" style={{ color: mine ? "rgba(212,175,55,0.7)" : "rgba(255,255,255,0.4)" }}>
                        {playing || currentTime > 0 ? formatAudioTime(currentTime) : formatAudioTime(duration)}
                    </span>
                </div>
            </div>
        </div>
    );
}

// ─── Main ProtectedAttachment ────────────────────────────────────────────────

export default function ProtectedAttachment({ attachment, mine, onMediaClick, testId }) {
    const [blobUrl, setBlobUrl] = useState(null);
    const [errored, setErrored] = useState(false);

    useEffect(() => {
        let active = true;
        let objectUrl = null;
        async function load() {
            try {
                setErrored(false);
                const res = await api.get(`/messages/attachments/${attachment.media_id || attachment.id}`, { responseType: "blob" });
                objectUrl = URL.createObjectURL(res.data);
                if (active) setBlobUrl(objectUrl);
            } catch (_) {
                if (active) setErrored(true);
            }
        }
        load();
        return () => {
            active = false;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [attachment.id, attachment.media_id]);

    if (errored) {
        return (
            <div className="rounded-2xl border px-4 py-3 text-xs text-white/55" style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.04)" }} data-testid={`${testId}-error`}>
                Attachment unavailable.
            </div>
        );
    }
    if (!blobUrl) {
        return (
            <div className="rounded-2xl border px-4 py-5 flex items-center justify-center" style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.04)" }} data-testid={`${testId}-loading`}>
                <Spinner size={18} className="animate-spin" color="#D4AF37" />
            </div>
        );
    }

    if (attachment.kind === "image") {
        return (
            <div
                className={`relative rounded-2xl overflow-hidden cursor-pointer group/att inline-block max-w-[280px] border ${
                    mine ? "border-[#D4AF37]/35" : "border-white/10"
                } shadow-[0_8px_24px_rgba(0,0,0,0.3)] transition-all duration-200 hover:scale-[1.01] active:scale-[0.99]`}
                onClick={() => onMediaClick?.({
                    type: "image",
                    url: blobUrl,
                    title: attachment.original_filename || "Passport photo",
                })}
                data-testid={testId}
                title="Click to view full photo"
            >
                <img
                    src={blobUrl}
                    alt={attachment.original_filename || "Message attachment"}
                    className="rounded-2xl w-full max-w-[280px] max-h-64 object-cover"
                />
            </div>
        );
    }
    if (attachment.kind === "video") {
        return (
            <div
                className={`relative rounded-2xl overflow-hidden cursor-pointer group/vid inline-block max-w-[280px] bg-black border ${
                    mine ? "border-[#D4AF37]/35" : "border-white/10"
                } shadow-[0_8px_24px_rgba(0,0,0,0.3)] transition-all duration-200 hover:scale-[1.01] active:scale-[0.99]`}
                onClick={() => onMediaClick?.({
                    type: "video",
                    url: blobUrl,
                    title: attachment.original_filename || "Passport video",
                })}
                data-testid={testId}
                title="Click to view full video"
            >
                <video
                    src={blobUrl}
                    playsInline
                    className="rounded-2xl w-full min-w-[240px] max-w-[280px] max-h-60 aspect-video object-contain bg-black pointer-events-none"
                />
            </div>
        );
    }
    if (attachment.kind === "document") {
        return (
            <a href={blobUrl} download={attachment.original_filename || "passport-file"} className="rounded-2xl border px-4 py-4 flex items-center justify-between gap-3 text-white/80" style={{ borderColor: mine ? "rgba(212,175,55,0.3)" : "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.04)" }} data-testid={testId}>
                <div className="flex items-center gap-3 min-w-0">
                    <div className="w-11 h-11 rounded-2xl grid place-items-center bg-white/8 text-white/70 shrink-0"><FileText size={18} /></div>
                    <div className="min-w-0">
                        <div className="text-sm text-white truncate">{attachment.original_filename || "Passport attachment"}</div>
                        <div className="text-xs text-white/45 truncate">{attachment.mime || "Document"}</div>
                    </div>
                </div>
                <FileArrowDown size={16} className="shrink-0" />
            </a>
        );
    }
    // Voice notes (kind === "audio" with is_voice flag) → luxury waveform player
    if (attachment.is_voice) {
        return <VoiceNotePlayer blobUrl={blobUrl} attachment={attachment} mine={mine} data-testid={testId} />;
    }
    // Generic audio fallback (non-voice audio files)
    return <audio src={blobUrl} controls className="w-full" data-testid={testId} />;
}
